import { Address } from '@electricjs/arc';
import {
  DeactivationCauseEnum,
  EmployeeStatusEnum,
  EmployeeLeaveStatusEnum,
  Employee as EmployeeDto,
} from '@electricjs/core_entity-client';
import { Asset } from '@/types/assets';
import { Group } from '@/types/groups';
import { Entitlement } from '@/types/organizations';
import { ChampionResponse } from '@/types/requests';
import { OrganizationUserRoles } from '@/types/users';
import { LicenseTypeOption } from '@/components/People/helpers/transformOptions';

export type EmployeeDirectoryListing = {
  id: string;
  firstName: string;
  preferredFirstName?: string;
  lastName: string;
  email: string | null;
  jobTitle?: string;
  jobTitleFrCa?: string | null;
  status: EmployeeStatusEnum;
  /** startDate  can't be a Date object inside Redux. It needs to be serializable (therefore, an ISO string) */
  startDate?: string;
};

export type Employee = {
  personalEmail?: string;
  userId?: string;
  /** @deprecated */
  devices?: Array<Record<string, string>>;
  createdAt?: string;
  updatedAt?: string;
  source: EmployeeSource;
  hrisIntegration?: HRISEmployeeIntegration;
  roles: OrganizationUserRoles;
  address?: Address;
  phone?: string;
  organizationPackageId?: string;
  entitlements?: Entitlement[];
  managerId?: string;
  terminationDate?: string;
  groupIds?: string[];
  deviceNotRequired?: boolean;
} & EmployeeDirectoryListing;

export type EmployeeWithAssets = Employee & {
  assets: Asset[];
};

export type EmployeeWithGroups = Employee & {
  groups: Group[];
  sortGroupName: string; // Which group name to sort by
};

export type EmployeeCount = {
  isEmployeeCountLoading: boolean;
  /** The total amount of ACTIVE (billable) employees of an organization */
  employeeCount: number;
};

export type EmployeeWithRequiredEmail = Employee & { email: string };

export type EmployeeApplicationAccount = {
  id: string;
  organizationId: string;
  employeeId: string;
  applicationId: string;
  createdAt: string;
  updatedAt: string;
};

export type CSVImportEmployee = {
  firstName: string;
  lastName: string;
  email: string;
  jobTitle?: string;
  personalEmail?: string;
  licenseType?: LicenseTypeOption;
  groups?: string[];
  managerEmail?: string;
  managerId?: string;
};

export type DesktopAppInstructionsArgs = {
  employeeIds: string[];
  organizationId: string;
};

export type MDMEnrollInstructionsArgs = {
  employeeIds: string[];
  organizationId: string;
};

export enum EmployeeSource {
  Manual = 'MANUAL',
  CsvUpload = 'CSV_UPLOAD',
  Hris = 'HRIS',
}

export type EmployeeUpdateArgs = {
  id: string;
  firstName?: string;
  lastName?: string;
  jobTitle?: string;
  status?: EmployeeStatusEnum;
  email?: string;
  personalEmail?: string;
  startDate?: string;
  address?: Address | null;
  phone?: string | null;
  organizationPackageId?: string;
  managerId?: string | null;
  deviceNotRequired?: boolean;
};

export type EmployeeStatusUpdateArgs = {
  id: string;
  status: EmployeeStatusEnum;
  deactivationCause?: DeactivationCauseEnum;
};

export type HRISEmployeeIntegration = {
  sourceId: string;
};

export type GetEmployeeByEmailArgs = {
  organizationId: string;
  email: string;
};

export type GetEmployeeByIdArgs = {
  organizationId: string;
  employeeId: string;
};

export type ListEmployeeApplicationAccountsArgs = {
  employeeId: string;
  organizationId: string;
};

export type GroupEmployeesResponse = {
  results: EmployeeDto[];
  total: number;
};

export type GroupEmployeesData = {
  employees: Employee[];
  total: number;
};

export type ListEmployeeApplicationAccountsResponse = {
  results: EmployeeApplicationAccount[];
  total: number;
};

export type BulkEmployeeApplicationAccountsArgs = {
  employeeIds: string[];
  organizationId: string;
};

export type GetApplicationEmployeeAccountsArgs = {
  organizationId: string;
  applicationKey: string;
  limit: number;
  offset?: number;
};

export type EmployeeApplicationAccountWithEmployee =
  EmployeeApplicationAccount & {
    employee: Employee | null;
  };

export type ApplicationEmployeeAccountsWithEmployeesResponse = {
  results: EmployeeApplicationAccountWithEmployee[];
  total: number;
};

export type BulkEmployeeApplicationAccountsResponse = {
  employeeApplicationAccountsMapping: Record<
    string,
    EmployeeApplicationAccount[]
  >;
};

export type BulkEmployeeGroupsResponse = {
  employeeGroups: Record<string, Group[]>;
};

export type ChampionWithEmployee = ChampionResponse &
  Partial<EmployeeDirectoryListing> & {
    /** The user can't see this employee (outside their divisions). */
    isRestricted?: boolean;
  };

export type BulkEmployeesArgs = {
  employeeIds: string[];
  organizationId: string;
};

export type BulkEmployeesResponse = {
  employeesMapping: Record<string, Employee>;
};

export type EmployeeStatusChangeLog = {
  id: string;
  employeeId: string;
  oldStatus?: EmployeeStatusEnum;
  newStatus: EmployeeStatusEnum;
  deactivationCause?: DeactivationCauseEnum;
  createdAt: string;
  updatedAt: string;
};

export type EmployeeStatusChangeLogResponse = {
  results: EmployeeStatusChangeLog[];
  total: number;
};

export type LeaveChampionReassignment = {
  id: string;
  substituteChampionId: string;
  requestCategoryId: string;
};

export type EmployeeLeave = {
  id: string;
  employeeId: string;
  startDate: string;
  // The generated DTO types this as `string | undefined`, but an open-ended
  // leave actually comes back as `return_date: null` - pydantic's
  // `Optional[date]` serializes `None` to JSON `null` rather than omitting
  // the field.
  returnDate?: string | null;
  status: EmployeeLeaveStatusEnum;
  defaultAssigneeId?: string;
  forwardeeEmployeeId?: string;
  championReassignments: LeaveChampionReassignment[];
  createdAt: string;
  updatedAt?: string;
};

export type EmployeeLeaveResponse = {
  results: EmployeeLeave[];
  total: number;
};
